import { forwardRef, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useIntl } from 'react-intl';
import { useBottomNavPresent } from '../layout/BottomTabBar';
import { useResponsiveLayout } from '../../hooks/ui/useResponsiveLayout';
import { overlayLayers } from '../../ui/overlayLayers';
import AiSparkButton from './AiSparkButton';
import { setAiDockOccupied, useAiDockSlot } from './aiDockSlot';

/** Sidebar dock (KAN-1147): 44 fits the 48px collapsed rail column (68 − 2×10 pad); 2px inset centres it. */
const SIDEBAR_AI_SIZE = 44;
const SIDEBAR_AI_INSET = 2;

/** How long the "answer ready" look plays after the assistant stops working. */
export const AI_DONE_MS = 2700;

/** thinking (busy) > done (just finished) > open (panel) > suggestion > rest. */
export function resolveAiButtonState({ busy, justDone, panelOpen, hasSuggestion }) {
  if (busy) return 'thinking';
  if (justDone) return 'done';
  if (panelOpen) return 'open';
  if (hasSuggestion) return 'suggestion';
  return 'rest';
}

/**
 * The CrewVex AI button (KAN-991). Touch: sits in the bottom bar's AI slot beside
 * the nav pill (fixed fallback on screens without the bar). Desktop: a fixed
 * button docked at the bottom of the left sidebar (KAN-1147) that widens into a labelled pill on hover;
 * it never moves and never covers the page, so pages keep no extra room at their end. A shell without
 * the sidebar falls back to a fixed corner button.
 *
 * KAN-1011: stays mounted while overlays are open. `hidden` hides it everywhere;
 * `hiddenUnlessDocked` hides it only off the bar — a docked button dims under the
 * overlay's scrim with the bar and comes back on the same frame.
 */
const AiFloatingFab = forwardRef(function AiFloatingFab(
  { onClick, hasSuggestion = false, panelOpen = false, busy = false, hidden = false, hiddenUnlessDocked = false },
  ref,
) {
  const intl = useIntl();
  const navPresent = useBottomNavPresent();
  const { isTablet } = useResponsiveLayout();
  // The docked button matches the bar it sits beside (KAN-994): phone pill 56, tablet dock 64.
  // The calm face (KAN-304 B) has no dark halo, so the phone needs no optical 54 any more.
  const dockedSize = isTablet ? 64 : 56;
  const { slot, sidebarSlot } = useAiDockSlot();

  const [justDone, setJustDone] = useState(false);
  const wasBusy = useRef(false);
  useEffect(() => {
    if (busy) {
      wasBusy.current = true;
      setJustDone(false);
      return undefined;
    }
    if (!wasBusy.current) return undefined;
    wasBusy.current = false;
    setJustDone(true);
    const t = setTimeout(() => setJustDone(false), AI_DONE_MS);
    return () => clearTimeout(t);
  }, [busy]);

  const docked = navPresent ? Boolean(slot) : Boolean(sidebarSlot);
  const visible = !hidden && (docked || !hiddenUnlessDocked);
  const portaled = docked && visible;
  useEffect(() => {
    setAiDockOccupied(portaled);
    return () => setAiDockOccupied(false);
  }, [portaled]);

  if (!visible) return null;

  const state = resolveAiButtonState({ busy, justDone, panelOpen, hasSuggestion });
  const ariaLabel = intl.formatMessage({ id: 'aiAssistant.fabAriaLabel', defaultMessage: 'Open CrewVex AI' });

  const hoverLabel = intl.formatMessage({ id: 'aiAssistant.fabHoverLabel', defaultMessage: 'Ask CrewVex AI' });

  if (portaled && !navPresent) {
    // Desktop: static at the bottom of the sidebar rail, centred on the collapsed rail column.
    return createPortal(
      <div data-cvx-ai-fab-wrap data-cvx-ai-sidebar-dock style={{ display: 'flex', margin: `0 0 10px ${SIDEBAR_AI_INSET}px` }}>
        <AiSparkButton ref={ref} size={SIDEBAR_AI_SIZE} variant="web" state={state} ariaLabel={ariaLabel} hoverLabel={hoverLabel} onClick={onClick} />
      </div>,
      sidebarSlot,
    );
  }

  if (portaled) {
    return createPortal(
      <div data-cvx-ai-fab-wrap style={{ display: 'flex', marginLeft: isTablet ? 12 : 8 }}>
        <AiSparkButton ref={ref} size={dockedSize} state={state} ariaLabel={ariaLabel} onClick={onClick} />
      </div>,
      slot,
    );
  }

  const fixedBase = { position: 'fixed', zIndex: overlayLayers.aiFab };
  if (navPresent) {
    return (
      <div
        className="cvx-ais-fab-wrap"
        style={{
          ...fixedBase,
          right: 'max(16px, env(safe-area-inset-right, 0px))',
          bottom: 'max(16px, calc(env(safe-area-inset-bottom, 0px) + 12px))',
        }}
      >
        <AiSparkButton ref={ref} size={56} state={state} ariaLabel={ariaLabel} onClick={onClick} />
      </div>
    );
  }
  return (
    <div
      className="cvx-ais-fab-wrap"
      style={{
        ...fixedBase,
        right: 'calc(28px + env(safe-area-inset-right, 0px))',
        bottom: 'calc(28px + env(safe-area-inset-bottom, 0px))',
      }}
    >
      <AiSparkButton ref={ref} size={52} variant="web" state={state} ariaLabel={ariaLabel} hoverLabel={hoverLabel} onClick={onClick} />
    </div>
  );
});

AiFloatingFab.displayName = 'AiFloatingFab';

export default AiFloatingFab;
